import { useState, useMemo } from "react";
import { useParams, useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  ArrowLeft,
  Calendar,
  Building2,
  DollarSign,
  Clock,
  MapPin,
  Users,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  Phone,
  Play,
  Search,
  Ban,
  Package,
  Edit,
  Eye,
  Info
} from "lucide-react";
import { useBooking, useUpdateBookingStatus, useConfirmVendorBooking } from "@/hooks/useBookings";
import type { BookingOptionResponse, BookingStatus } from "@/types/activities";
import { getBookingStatusColor, getBookingOptionStatusColor, BookingOptionStatus } from "@/types/activities";
import FinalBookingConfirmation from "@/components/FinalBookingConfirmation";
import CancelBookingModal from "@/components/CancelBookingModal";
import BreadcrumbNavigation from "@/components/BreadcrumbNavigation";
import BookingWorkflowProgress from "@/components/BookingWorkflowProgress";
import UpdateBookingOptionModal from "@/components/UpdateBookingOptionModal";
import BookingOptionsList from "@/components/BookingOptionsList";
import ErrorBoundary from "@/components/ui/ErrorBoundary";
import { useToast } from "@/hooks/use-toast";

const getStatusIcon = (status: BookingStatus) => {
  switch (status) {
    case "Confirmed": return <CheckCircle className="w-4 h-4 text-green-500" />;
    case "Cancelled": return <XCircle className="w-4 h-4 text-red-500" />;
    case "NoVendorsAvailable": return <Ban className="w-4 h-4 text-red-500" />;
    case "PendingVendorContact": return <Clock className="w-4 h-4 text-blue-500" />;
    case "VendorNegotiating": return <Phone className="w-4 h-4 text-yellow-500" />;
    case "PendingOrganizationApproval": return <Eye className="w-4 h-4 text-purple-500" />;
    case "ApprovedAwaitingVendorBooking": return <Calendar className="w-4 h-4 text-orange-500" />;
    default: return <AlertCircle className="w-4 h-4 text-gray-500" />;
  }
};

const getOptionStatusIcon = (status: BookingOptionStatus) => {
  switch (status) {
    case BookingOptionStatus.Exploring: return '🔍';
    case BookingOptionStatus.Available: return '✅';
    case BookingOptionStatus.Unavailable: return '❌';
    case BookingOptionStatus.Rejected: return '👎';
    case BookingOptionStatus.Approved: return '👍';
    default: return '⏳';
  }
};

export default function BookingDetailPage() {
  const { bookingId } = useParams<{ bookingId: string }>();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const [showUpdateOptionModal, setShowUpdateOptionModal] = useState(false);
  const [showFinalConfirmationModal, setShowFinalConfirmationModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedOption, setSelectedOption] = useState<BookingOptionResponse | null>(null);

  const { data: booking, loading, error, refetch } = useBooking(bookingId!);
  const updateBookingStatus = useUpdateBookingStatus();
  const confirmVendorBooking = useConfirmVendorBooking();

  // Get the active booking option (the one currently being worked on)
  const activeOption = useMemo(() => {
    if (!booking?.bookingOptions || booking.bookingOptions.length === 0) return null;

    // Find the first option that's not Rejected or Unavailable
    return booking.bookingOptions.find(
      opt => opt.status !== BookingOptionStatus.Rejected &&
             opt.status !== BookingOptionStatus.Unavailable
    ) || null;
  }, [booking?.bookingOptions]);

  // Get rejected/unavailable options for history
  const previousOptions = useMemo(() => {
    if (!booking?.bookingOptions) return [];
    return booking.bookingOptions.filter(
      opt => opt.status === BookingOptionStatus.Rejected ||
             opt.status === BookingOptionStatus.Unavailable
    ).sort((a, b) => b.optionOrder - a.optionOrder);
  }, [booking?.bookingOptions]);

  // Can add new option only if all existing options are rejected/unavailable
  const canAddNewOption = useMemo(() => {
    if (!booking?.bookingOptions || booking.bookingOptions.length === 0) return false;
    return booking.bookingOptions.every(
      opt => opt.status === BookingOptionStatus.Rejected ||
             opt.status === BookingOptionStatus.Unavailable
    );
  }, [booking?.bookingOptions]);

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "Not specified";
    return new Date(dateString).toLocaleDateString();
  };

  const formatPrice = (price?: number) => {
    if (!price) return "Pending";
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED'
    }).format(price);
  };

  // Handle "Start Working" button - changes status from PendingVendorContact to VendorNegotiating
  const handleStartWorking = async () => {
    try {
      await updateBookingStatus.mutateAsync({
        bookingId: bookingId!,
        data: {
          status: "VendorNegotiating" as BookingStatus,
          notes: "Athlyn team started working on this booking"
        }
      });

      toast({
        title: "Status updated",
        description: "You're now working on this booking. Update Option #1 with vendor response."
      });

      refetch();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update booking status",
        variant: "destructive"
      });
    }
  };

  const handleEditOption = (option: BookingOptionResponse) => {
    setSelectedOption(option);
    setShowUpdateOptionModal(true);
  };

  const handleConfirmBooking = () => {
    if (activeOption && activeOption.status === BookingOptionStatus.Approved) {
      setSelectedOption(activeOption);
      setShowFinalConfirmationModal(true);
    }
  };

  const breadcrumbItems = [
    { label: "Dashboard", href: "/dashboard" },
    { label: "Bookings", href: "/bookings" },
    { label: booking ? `${booking.activityName}` : "Loading...", href: "#", current: true }
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <BreadcrumbNavigation items={breadcrumbItems} />
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-64 w-full" />
          <div className="grid grid-cols-3 gap-4">
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="space-y-6">
        <BreadcrumbNavigation items={breadcrumbItems} />
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error || "Booking not found"}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <BreadcrumbNavigation items={breadcrumbItems} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => setLocation("/bookings")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Bookings
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <Package className="w-5 h-5 text-primary" />
              </div>
              {booking.activityName || "Booking Request"}
            </h1>
            <p className="text-muted-foreground flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              {booking.organizationName}
              <span className="text-muted-foreground">•</span>
              {booking.estimatedParticipantsCount} participants
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {getStatusIcon(booking.status)}
          <Badge
            variant="outline"
            className={`${getBookingStatusColor(booking.status)}`}
          >
            {booking.status.replace(/([A-Z])/g, ' $1').trim()}
          </Badge>
        </div>
      </div>

      <Separator />

      <ErrorBoundary level="section">
        <div className="space-y-6">
          {/* Workflow Progress */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Booking Workflow Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              <BookingWorkflowProgress
                currentStatus={booking.status}
                orientation="horizontal"
                showDescriptions={true}
              />
            </CardContent>
          </Card>

          {/* Primary Action - Start Working Button */}
          {booking.status === "PendingVendorContact" && (
            <Alert className="bg-blue-50 border-blue-200">
              <Info className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                <div className="flex items-center justify-between">
                  <div>
                    <strong className="block mb-1">Next Step: Start Working on This Booking</strong>
                    <p className="text-sm">
                      Click the button to change status to "Vendor Negotiating" and begin working on Option #1.
                    </p>
                  </div>
                  <Button
                    onClick={handleStartWorking}
                    disabled={updateBookingStatus.isPending}
                    className="ml-4 flex items-center gap-2"
                  >
                    <Play className="w-4 h-4" />
                    {updateBookingStatus.isPending ? "Starting..." : "Start Working"}
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Active Booking Option */}
          {activeOption && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Current Booking Option (Option #{activeOption.optionOrder})
                  </CardTitle>
                  <Badge variant="outline" className={getBookingOptionStatusColor(activeOption.status)}>
                    {getOptionStatusIcon(activeOption.status)} {activeOption.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Auto-created notice for Option #1 */}
                {activeOption.optionOrder === 1 && (
                  <Alert className="bg-blue-50 border-blue-200">
                    <Info className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-blue-800 text-sm">
                      <strong>🤖 Auto-Created:</strong> This option was automatically created when the organization submitted their booking request.
                      {activeOption.status === BookingOptionStatus.Exploring && (
                        <> Update it with the vendor's response below.</>
                      )}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Option Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Service</p>
                    <p className="text-sm">{activeOption.serviceName || 'Service'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Service Option</p>
                    <p className="text-sm">{activeOption.serviceOptionName || 'Option'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Vendor</p>
                    <p className="text-sm">{activeOption.vendorName || 'Unknown'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Marketplace Price</p>
                    <p className="text-sm">{formatPrice(activeOption.marketplacePrice)}</p>
                  </div>
                </div>

                {/* Available Option Details */}
                {activeOption.status === BookingOptionStatus.Available && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-3">
                    <h4 className="text-sm font-medium text-green-800 flex items-center gap-2">
                      ✅ Vendor Available - Proposal Details
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <div>
                          <p className="text-xs text-green-700 font-medium">Proposed Price</p>
                          <p className="font-semibold text-green-900">{formatPrice(activeOption.proposedPrice)}</p>
                        </div>
                      </div>
                      {activeOption.proposedCapacity && (
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-green-700" />
                          <div>
                            <p className="text-xs text-green-700 font-medium">Capacity</p>
                            <p className="font-semibold text-green-900">{activeOption.proposedCapacity} people</p>
                          </div>
                        </div>
                      )}
                      {activeOption.proposedStartDate && (
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-green-700" />
                          <div>
                            <p className="text-xs text-green-700 font-medium">Proposed Dates</p>
                            <p className="font-semibold text-green-900">
                              {formatDate(activeOption.proposedStartDate)}
                              {activeOption.proposedEndDate && ` - ${formatDate(activeOption.proposedEndDate)}`}
                            </p>
                          </div>
                        </div>
                      )}
                      {activeOption.proposedLocation && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-green-700" />
                          <div>
                            <p className="text-xs text-green-700 font-medium">Location</p>
                            <p className="font-semibold text-green-900 truncate">{activeOption.proposedLocation}</p>
                          </div>
                        </div>
                      )}
                    </div>
                    {activeOption.specialTerms && (
                      <div className="pt-2">
                        <p className="text-xs text-green-700 font-medium mb-1">Special Terms</p>
                        <p className="text-sm text-green-900 bg-white/50 p-2 rounded">{activeOption.specialTerms}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Unavailable Option */}
                {activeOption.status === BookingOptionStatus.Unavailable && activeOption.unavailableReason && (
                  <Alert variant="destructive">
                    <XCircle className="h-4 w-4" />
                    <AlertDescription>
                      <strong>Vendor Unavailable:</strong> {activeOption.unavailableReason}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Rejected by Organization */}
                {activeOption.status === BookingOptionStatus.Rejected && (
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg space-y-2">
                    <h4 className="text-sm font-medium text-orange-800 flex items-center gap-2">
                      👎 Rejected by Organization
                    </h4>
                    {activeOption.rejectionReason && (
                      <p className="text-sm text-orange-700">{activeOption.rejectionReason}</p>
                    )}
                    {activeOption.counterStartDate && (
                      <div className="text-sm text-orange-700">
                        <strong>Counter-Offer:</strong> Dates: {formatDate(activeOption.counterStartDate)} - {formatDate(activeOption.counterEndDate)}
                        {activeOption.counterCapacity && `, Capacity: ${activeOption.counterCapacity}`}
                      </div>
                    )}
                  </div>
                )}

                {/* Approved by Organization */}
                {activeOption.status === BookingOptionStatus.Approved && (
                  <Alert className="bg-blue-50 border-blue-200">
                    <CheckCircle className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-blue-800">
                      <strong>✓ Approved by Organization</strong> - Ready for vendor booking confirmation
                      {activeOption.respondedByEmployeeName && (
                        <span className="ml-2">by {activeOption.respondedByEmployeeName}</span>
                      )}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Action Buttons */}
                <div className="flex items-center gap-2 pt-2">
                  {activeOption.status === BookingOptionStatus.Exploring && (
                    <Button
                      onClick={() => handleEditOption(activeOption)}
                      className="flex items-center gap-2"
                    >
                      <Edit className="w-4 h-4" />
                      Update with Vendor Response
                    </Button>
                  )}

                  {activeOption.status === BookingOptionStatus.Available && (
                    <Button
                      onClick={() => handleEditOption(activeOption)}
                      variant="outline"
                      className="flex items-center gap-2"
                    >
                      <Edit className="w-4 h-4" />
                      Edit Proposal
                    </Button>
                  )}

                  {booking.status === "ApprovedAwaitingVendorBooking" && activeOption.status === BookingOptionStatus.Approved && (
                    <Button
                      onClick={handleConfirmBooking}
                      className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Confirm Vendor Booking
                    </Button>
                  )}

                  <Button
                    onClick={() => setShowCancelModal(true)}
                    variant="outline"
                    className="ml-auto text-red-600 hover:text-red-700"
                  >
                    Cancel Booking
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Booking Options List - Always show for adding alternatives */}
          <BookingOptionsList
            bookingId={bookingId!}
            bookingStatus={booking.status}
            canAddOptions={true}
          />

          {/* Cannot add new option alert */}
          {!canAddNewOption && activeOption && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                <strong>Note:</strong> You can only add a new booking option after the current option is either rejected by the organization or marked as unavailable by the vendor.
              </AlertDescription>
            </Alert>
          )}

          {/* Booking Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Booking Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Booking ID</p>
                  <p className="text-sm font-mono">{booking.id.slice(0, 8)}...</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Organization</p>
                  <p className="text-sm">{booking.organizationName}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Activity</p>
                  <p className="text-sm">{booking.activityName}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Created</p>
                  <p className="text-sm flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDateTime(booking.createdAt)}
                  </p>
                </div>
              </div>

              <Separator />

              {/* Requirements */}
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-3">Requirements</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Participants</p>
                    <p className="text-sm flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {booking.estimatedParticipantsCount}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Preferred Date</p>
                    <p className="text-sm flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(booking.preferredStartDate)}
                      {booking.preferredEndDate && ` - ${formatDate(booking.preferredEndDate)}`}
                    </p>
                  </div>
                </div>
              </div>

              {booking.specialRequirements && (
                <>
                  <Separator />
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">Special Requirements</p>
                    <p className="text-sm bg-muted/30 p-3 rounded-md">{booking.specialRequirements}</p>
                  </div>
                </>
              )}

              {/* Original Service Request */}
              {booking.originalServiceOptionName && (
                <>
                  <Separator />
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-3">Original Service Request</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Service Option</p>
                        <p className="text-sm">{booking.originalServiceOptionName}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Vendor</p>
                        <p className="text-sm">{booking.originalVendorName || 'Not specified'}</p>
                      </div>
                      {booking.originalMarketplacePrice && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Marketplace Price</p>
                          <p className="text-sm">{formatPrice(booking.originalMarketplacePrice)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Previous Options History */}
          {previousOptions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Previous Options ({previousOptions.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {previousOptions.map((option) => (
                  <div key={option.id} className="p-3 bg-muted/30 rounded-lg border">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{getOptionStatusIcon(option.status)}</span>
                        <div>
                          <p className="text-sm font-medium">Option #{option.optionOrder}: {option.serviceOptionName}</p>
                          <p className="text-xs text-muted-foreground">{option.vendorName}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className={getBookingOptionStatusColor(option.status)}>
                        {option.status}
                      </Badge>
                    </div>
                    {option.unavailableReason && (
                      <p className="text-xs text-muted-foreground">Reason: {option.unavailableReason}</p>
                    )}
                    {option.rejectionReason && (
                      <p className="text-xs text-muted-foreground">Rejection: {option.rejectionReason}</p>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Status Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Status Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-muted/30 p-3 rounded-lg">
                  <p className="text-lg font-semibold">{booking.totalOptionsExplored || 0}</p>
                  <p className="text-xs text-muted-foreground">Options Explored</p>
                </div>
                <div className="bg-muted/30 p-3 rounded-lg">
                  <p className="text-lg font-semibold">{booking.vendorsContacted || 0}</p>
                  <p className="text-xs text-muted-foreground">Vendors Contacted</p>
                </div>
                <div className="bg-muted/30 p-3 rounded-lg">
                  <p className="text-lg font-semibold">{booking.availableOptionsCount || 0}</p>
                  <p className="text-xs text-muted-foreground">Available Options</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </ErrorBoundary>

      {/* Modals */}
      {selectedOption && (
        <UpdateBookingOptionModal
          open={showUpdateOptionModal}
          onClose={() => {
            setShowUpdateOptionModal(false);
            setSelectedOption(null);
          }}
          bookingId={bookingId!}
          option={selectedOption}
          onSuccess={() => {
            refetch();
            setShowUpdateOptionModal(false);
            setSelectedOption(null);
          }}
        />
      )}

      {selectedOption && (
        <FinalBookingConfirmation
          open={showFinalConfirmationModal}
          onClose={() => {
            setShowFinalConfirmationModal(false);
            setSelectedOption(null);
          }}
          booking={booking}
          selectedOption={selectedOption}
          onConfirm={async (confirmationData) => {
            try {
              await confirmVendorBooking.mutateAsync({
                bookingId: bookingId!,
                data: {
                  vendorBookingReference: confirmationData.vendorBookingReference,
                  vendorBookedAt: confirmationData.vendorBookedAt ? new Date(confirmationData.vendorBookedAt).toISOString() : undefined,
                  finalPrice: confirmationData.finalPrice,
                  finalLocation: confirmationData.finalLocation,
                  finalCapacity: confirmationData.finalCapacity,
                  confirmationNotes: confirmationData.confirmationNotes
                }
              });

              toast({
                title: "Booking Confirmed!",
                description: "The vendor booking has been confirmed successfully. The organization will be notified."
              });

              refetch();
              setShowFinalConfirmationModal(false);
              setSelectedOption(null);
            } catch (error: any) {
              toast({
                title: "Confirmation Failed",
                description: error.message || "Failed to confirm vendor booking. Please try again.",
                variant: "destructive"
              });
            }
          }}
        />
      )}

      <CancelBookingModal
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        booking={booking}
        onSuccess={() => {
          setShowCancelModal(false);
          setLocation("/bookings");
        }}
      />
    </div>
  );
}
